/**
 * LiveChat — an agent's manual invite lands in the bubble, never fullscreen.
 * ─────────────────────────────────────────────────────────────────────────────
 * When an agent sends a manual invite the widget maximizes itself, which on a
 * phone means fullscreen over the page the shopper was reading. We push it back
 * to the bubble, where LiveChat keeps its own unread badge.
 *
 * Event order, read off the live widget (this is the whole design):
 *
 *   agent invite            shopper opens it themselves
 *   ─────────────────────   ───────────────────────────
 *   customer_status_changed  visibility_changed: maximized
 *     → status: 'chatting'   (…they type…)
 *   visibility_changed        customer_status_changed
 *     → maximized               → status: 'chatting'
 *   new_event (the message)
 *
 * So `customer_status_changed` landing JUST BEFORE a maximize is the invite,
 * and the same event after a maximize is the shopper. `greeting_displayed`
 * never fires for a manual invite, and `new_event` arrives after the window is
 * already open — neither can gate this, which is why an earlier build that
 * waited on them never suppressed anything.
 *
 * `new_event` is deliberately NOT evidence either: a later agent message does
 * not re-open the widget, so it would only ever fire while the widget is
 * already minimized — right when the shopper sees the badge and taps it. That
 * tap would look like an invite and get closed on them.
 *
 * Status only turns to 'chatting' once per session, so the one-shot guard below
 * permanently retires that evidence after the first suppression.
 *
 * The rule stays one-sided: suppress only with fresh agent evidence AND no sign
 * of the shopper, and only once per piece of evidence. A missed invite is
 * cosmetic; closing a chat the shopper just opened makes support unreachable,
 * so the shopper's next tap always gets through.
 *
 * Their taps are hard to see — the bubble lives in LiveChat's iframe, so clicks
 * never reach our document. Focus is the tell: it sits in the widget's iframe,
 * and the window blur that put it there fires even for a cross-origin frame.
 *
 * Second job, same event: when the chat ends the widget stays open on its
 * end-of-chat screen. 'browsing' is that moment, so we minimize there too,
 * provided the session has a chatId (otherwise 'browsing' is just an idle
 * visitor). Unlike the invite half this is not gated on a takeover — a
 * finished conversation has no reason to hold the panel open at any width.
 *
 * Add ?mblcdebug=1 to any URL to log every decision to the console.
 */

// The widget's own fullscreen switch, not the theme's 750px gutter breakpoint.
// Above it LiveChat opens a corner panel, which is fine to leave alone.
const MB_LC_MOBILE_QUERY = '(max-width: 767px)';
// Second, breakpoint-free read of the same question: if the open widget covers
// this much of the viewport it IS the takeover we were asked to prevent.
const MB_LC_TAKEOVER_RATIO = 0.85;
const MB_LC_AGENT_GRACE_MS = 2000; // the maximize follows its status change in ms
const MB_LC_INTENT_MS = 3000;
const MB_LC_READY_TIMEOUT_MS = 30000;
const MB_LC_POLL_MS = 250;
const MB_LC_CONTAINER_ID = 'chat-widget-container';
const MB_LC_LAUNCHER_ID = 'chat-widget-minimized';

const mbLcDebug = new URLSearchParams(location.search).has('mblcdebug');
const mbLcLog = (...args) => {
  if (mbLcDebug) console.log('%c[mb-lc]', 'color:#0a0;font-weight:700', ...args);
};

let mbLcAgentEventAt = 0;
let mbLcShopperIntentAt = 0;
let mbLcSuppressedEventAt = 0;
let mbLcLastFocused = null;
let mbLcAnnouncer = null;

const mbLcRecent = (stamp, within) => stamp > 0 && Date.now() - stamp < within;

const mbLcWidgetRoot = () => document.getElementById(MB_LC_CONTAINER_ID);

// Only the iframes: the container itself can be a full-viewport pass-through
// wrapper, which would read as a takeover even while the bubble is closed.
const mbLcTakeoverRatio = () => {
  const frames = mbLcWidgetRoot()?.querySelectorAll('iframe');
  if (!frames?.length) return 0;
  return [...frames].reduce((max, frame) => {
    const rect = frame.getBoundingClientRect();
    const cover = Math.min(rect.width / window.innerWidth, rect.height / window.innerHeight);
    return Math.max(max, cover);
  }, 0);
};

const mbLcIsTakeover = () =>
  window.matchMedia(MB_LC_MOBILE_QUERY).matches || mbLcTakeoverRatio() >= MB_LC_TAKEOVER_RATIO;

const mbLcFocusIsInWidget = () => {
  const el = document.activeElement;
  if (!el || el.tagName !== 'IFRAME') return false;
  return Boolean(el.closest(`#${MB_LC_CONTAINER_ID}`)) || /livechat/i.test(el.src || '');
};

const mbLcMarkShopperIntent = (source) => {
  mbLcShopperIntentAt = Date.now();
  mbLcLog('shopper intent:', source);
};

const mbLcAnnounce = (message) => {
  if (!mbLcAnnouncer) {
    mbLcAnnouncer = document.createElement('p');
    mbLcAnnouncer.className = 'visually-hidden';
    mbLcAnnouncer.setAttribute('aria-live', 'polite');
    document.body.appendChild(mbLcAnnouncer);
  }
  mbLcAnnouncer.textContent = message;
};

// The widget pulls focus into its iframe as it opens, and minimizing leaves it
// stranded on a frame that is now hidden. Hand it back to wherever the shopper
// was; failing that, to the launcher, which is the visible thing that replaced
// the panel. Measured: without this, activeElement stays on #chat-widget after
// the window closes.
const mbLcRestoreFocus = () => {
  requestAnimationFrame(() => {
    const active = document.activeElement;
    const stranded = !active || active === document.body || mbLcFocusIsInWidget();
    if (!stranded) return;

    const target = mbLcLastFocused?.isConnected
      ? mbLcLastFocused
      : document.getElementById(MB_LC_LAUNCHER_ID);
    target?.focus({ preventScroll: true });
  });
};

document.addEventListener('focusin', (event) => {
  const el = event.target;
  if (el instanceof HTMLElement && !el.closest(`#${MB_LC_CONTAINER_ID}`)) mbLcLastFocused = el;
});

// Clicks inside the widget's iframe never reach us, but focusing it blurs the
// window — that blur is our proof the shopper reached for the bubble.
window.addEventListener('blur', () => {
  if (mbLcFocusIsInWidget()) mbLcMarkShopperIntent('iframe blur');
});

// LiveChat console.errors an unknown event name rather than throwing, so the
// try/catch can't hide one — only register names this build actually has.
// `greeting_accepted` is NOT one of them; shopper intent comes from focus.
const mbLcOn = (widget, event, handler) => {
  try {
    widget.on(event, handler);
  } catch (error) {
    mbLcLog('event not available:', event, error.message);
  }
};

const mbLcWire = (widget) => {
  mbLcLog('wired');

  // Both of these are asked at decision time, never cached at startup. Caching
  // was wrong twice: `LiveChatWidget` exists as a call-queue stub with working
  // `on`/`get` methods before the real widget is up, so an early read is a
  // stale answer, and a flag set from the 'chatting' event misses a shopper who
  // arrives on a page mid-conversation — that transition happened on the
  // previous page. By the time these fire, the widget is genuinely running.
  const mbLcIsMaximized = () => {
    try {
      return widget.get('state')?.visibility === 'maximized';
    } catch {
      return false;
    }
  };

  // chatId outlives the chat it belongs to, so it answers "was there ever a
  // chat in this session" on any page.
  const mbLcHadChat = () => {
    try {
      return Boolean(widget.get('chat_data')?.chatId);
    } catch {
      return false;
    }
  };

  // One event carries both halves of this file. Going INTO 'chatting' without
  // the shopper asking is the invite; coming back out to 'browsing' is the
  // conversation ending, which otherwise leaves the widget parked on its
  // end-of-chat screen.
  mbLcOn(widget, 'customer_status_changed', (data) => {
    const status = data?.status;
    mbLcLog('status:', status);

    if (status === 'chatting') {
      mbLcAgentEventAt = Date.now();
      return;
    }

    if (status !== 'browsing') return;
    if (!mbLcIsMaximized() || !mbLcHadChat()) return;

    widget.call('minimize');
    mbLcRestoreFocus();
    mbLcLog('chat ended → minimized');
  });

  mbLcOn(widget, 'visibility_changed', (data) => {
    if (data?.visibility !== 'maximized') return;

    if (mbLcFocusIsInWidget()) mbLcMarkShopperIntent('focus in widget');

    const takeover = mbLcIsTakeover();
    const shopper = mbLcRecent(mbLcShopperIntentAt, MB_LC_INTENT_MS);
    const agent = mbLcRecent(mbLcAgentEventAt, MB_LC_AGENT_GRACE_MS);
    const fresh = mbLcAgentEventAt > mbLcSuppressedEventAt;
    mbLcLog('maximized —', { takeover, shopper, agent, fresh, width: window.innerWidth });

    if (!takeover || shopper || !agent || !fresh) return;

    mbLcSuppressedEventAt = mbLcAgentEventAt;
    widget.call('minimize');
    mbLcRestoreFocus();
    // The unread badge lives inside LiveChat's iframe; say it out loud too,
    // since we just removed the panel that used to be the notification.
    mbLcAnnounce('New message from support. Open the chat bubble to read it.');
    mbLcLog('suppressed → minimized');
  });
};

const mbLcWhenReady = (onWidget) => {
  const startedAt = Date.now();
  const tick = () => {
    const widget = window.LiveChatWidget;
    if (typeof widget?.on === 'function') {
      onWidget(widget);
      return;
    }
    if (Date.now() - startedAt > MB_LC_READY_TIMEOUT_MS) {
      mbLcLog('gave up — LiveChatWidget never appeared');
      return;
    }
    setTimeout(tick, MB_LC_POLL_MS);
  };
  tick();
};

mbLcWhenReady(mbLcWire);
